/**
 * Collaboration Service
 *
 * API service for Lead Collaboration operations.
 * Handles team collaboration on leads, invitations, member management,
 * and commission distribution.
 *
 * Base URL: /api/leadflow/collaborations
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

/**
 * Collaboration status options
 */
export const COLLABORATION_STATUS = {
    active: {
        value: 'active',
        label: 'Active',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        icon: 'pi-play-circle',
        severity: 'success'
    },
    completed: {
        value: 'completed',
        label: 'Completed',
        color: '#3B82F6',
        bgColor: '#DBEAFE',
        icon: 'pi-check-circle',
        severity: 'info'
    },
    cancelled: {
        value: 'cancelled',
        label: 'Cancelled',
        color: '#EF4444',
        bgColor: '#FEE2E2',
        icon: 'pi-times-circle',
        severity: 'danger'
    },
    disputed: {
        value: 'disputed',
        label: 'Disputed',
        color: '#F97316',
        bgColor: '#FFEDD5',
        icon: 'pi-exclamation-triangle',
        severity: 'warning'
    }
};

/**
 * Member role options
 */
export const MEMBER_ROLES = {
    owner: {
        value: 'owner',
        label: 'Owner',
        color: '#8B5CF6',
        bgColor: '#EDE9FE',
        icon: 'pi-crown',
        description: 'Lead owner and primary collaborator'
    },
    collaborator: {
        value: 'collaborator',
        label: 'Collaborator',
        color: '#3B82F6',
        bgColor: '#DBEAFE',
        icon: 'pi-users',
        description: 'Active team member working on the lead'
    },
    consultant: {
        value: 'consultant',
        label: 'Consultant',
        color: '#14B8A6',
        bgColor: '#CCFBF1',
        icon: 'pi-user-edit',
        description: 'Advisory role with specific expertise'
    }
};

/**
 * Commission model options
 */
export const COMMISSION_MODELS = {
    percentage_split: {
        value: 'percentage_split',
        label: 'Percentage Split',
        icon: 'pi-chart-pie',
        description: 'Commission divided by percentages (e.g., 60%, 25%, 15%)'
    },
    fixed_amount: {
        value: 'fixed_amount',
        label: 'Fixed Amount',
        icon: 'pi-indian-rupee',
        description: 'Each member gets a fixed amount based on role'
    },
    milestone_based: {
        value: 'milestone_based',
        label: 'Milestone Based',
        icon: 'pi-flag',
        description: 'Commission paid on milestone completion'
    }
};

/**
 * Contribution level options
 */
export const CONTRIBUTION_LEVELS = {
    high: {
        value: 'high',
        label: 'High',
        color: '#22C55E',
        bgColor: '#DCFCE7',
        description: 'Major contribution to the deal'
    },
    medium: {
        value: 'medium',
        label: 'Medium',
        color: '#EAB308',
        bgColor: '#FEF9C3',
        description: 'Moderate contribution to the deal'
    },
    low: {
        value: 'low',
        label: 'Low',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        description: 'Minor or advisory contribution'
    }
};

/**
 * Member status options
 */
export const MEMBER_STATUS = {
    invited: { value: 'invited', label: 'Invited', color: '#EAB308', icon: 'pi-envelope' },
    active: { value: 'active', label: 'Active', color: '#22C55E', icon: 'pi-check' },
    completed: { value: 'completed', label: 'Completed', color: '#3B82F6', icon: 'pi-check-circle' },
    left: { value: 'left', label: 'Left', color: '#6B7280', icon: 'pi-sign-out' }
};

/**
 * Dispute reason options
 */
export const DISPUTE_REASONS = [
    { value: 'commission_disagreement', label: 'Commission Disagreement' },
    { value: 'role_mismatch', label: 'Role/Contribution Mismatch' },
    { value: 'lead_quality', label: 'Lead Quality Issues' },
    { value: 'member_not_contributing', label: 'Member Not Contributing' },
    { value: 'communication_breakdown', label: 'Communication Breakdown' },
    { value: 'fraudulent_activity', label: 'Fraudulent Activity' },
    { value: 'other', label: 'Other' }
];

/**
 * Dispute resolution options
 */
export const DISPUTE_RESOLUTIONS = [
    { value: 'renegotiate_commission', label: 'Renegotiate Commission Split' },
    { value: 'remove_member', label: 'Remove Specific Member' },
    { value: 'cancel_collaboration', label: 'Cancel Lead Deal' },
    { value: 'mediation', label: 'Mediation with Support' },
    { value: 'other', label: 'Other' }
];

/**
 * Cancellation reason options
 */
export const CANCELLATION_REASONS = [
    { value: 'lead_unavailable', label: 'Lead No Longer Available' },
    { value: 'borrower_withdrew', label: 'Borrower Withdrew' },
    { value: 'quality_issues', label: 'Quality Issues' },
    { value: 'unable_to_proceed', label: 'Unable to Proceed' },
    { value: 'other', label: 'Other' }
];

// ================================
// API SERVICE METHODS
// ================================

export const collaborationService = {
    /**
     * Get my collaborations with filters
     * @param {Object} filters - Filter parameters
     * @param {string} filters.status - Filter by status (active, completed, cancelled, disputed)
     * @param {string} filters.role - Filter by role (owner, collaborator, all)
     * @param {number} filters.per_page - Items per page
     * @param {number} filters.page - Page number
     * @param {string} filters.search - Search query (borrower name, phone, email, city, tracking code)
     * @param {string} filters.commission_model - Filter by commission model (percentage_split, fixed_amount, milestone_based)
     * @returns {Promise<Object>} Paginated collaborations with stats
     */
    getMyCollaborations: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            const filterKeys = ['status', 'role', 'per_page', 'page', 'search', 'commission_model', 'loan_type', 'date_from', 'date_to'];
            filterKeys.forEach(key => {
                if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/collaborations?${query}` : '/leadflow/collaborations';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('collaborationService.getMyCollaborations:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get pending invitations for current user
     * @returns {Promise<Object>} List of pending invitations
     */
    getInvitations: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/collaborations/invitations');
            return response.data;
        } catch (error) {
            console.error('collaborationService.getInvitations:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get collaboration details
     * @param {string|number} collaborationId - Collaboration ID
     * @returns {Promise<Object>} Collaboration details with members
     */
    getCollaborationDetails: async (collaborationId) => {
        try {
            const response = await axiosInstance.get(`/leadflow/collaborations/${collaborationId}`);
            return response.data;
        } catch (error) {
            console.error('collaborationService.getCollaborationDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Invite a member to collaboration
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} memberData - Member invitation data
     * @param {number} memberData.profile_id - Profile ID of user to invite
     * @param {string} memberData.role - Role (collaborator, consultant)
     * @param {number} memberData.commission_percentage - For percentage_split model
     * @param {number} memberData.commission_fixed_amount - For fixed_amount model
     * @param {Array} memberData.milestones - For milestone_based model
     * @param {string} memberData.notes - Personal message to invitee
     * @param {string} memberData.contribution_level - Expected contribution (high, medium, low)
     * @returns {Promise<Object>} Invitation result
     */
    inviteMember: async (collaborationId, memberData) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/invite`,
                memberData
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.inviteMember:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Accept a collaboration invitation
     * @param {string|number} invitationId - Invitation/Member ID
     * @returns {Promise<Object>} Acceptance result
     */
    acceptInvitation: async (invitationId) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/invitations/${invitationId}/accept`
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.acceptInvitation:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Decline a collaboration invitation
     * @param {string|number} invitationId - Invitation/Member ID
     * @param {Object} data - Optional decline data
     * @param {string} data.reason - Reason for declining
     * @param {string} data.notes - Additional notes
     * @returns {Promise<Object>} Decline result
     */
    declineInvitation: async (invitationId, data = {}) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/invitations/${invitationId}/decline`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.declineInvitation:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Leave a collaboration (for non-owner members)
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} data - Optional leave data
     * @param {string} data.reason - Reason for leaving
     * @returns {Promise<Object>} Leave result
     */
    leaveCollaboration: async (collaborationId, data = {}) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/leave`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.leaveCollaboration:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Remove a member from collaboration (owner only)
     * @param {string|number} collaborationId - Collaboration ID
     * @param {string|number} memberId - Member ID to remove
     * @param {Object} data - Optional removal data
     * @param {string} data.reason - Reason for removal
     * @returns {Promise<Object>} Removal result
     */
    removeMember: async (collaborationId, memberId, data = {}) => {
        try {
            const response = await axiosInstance.delete(
                `/leadflow/collaborations/${collaborationId}/members/${memberId}`,
                { data }
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.removeMember:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Update member details (owner only)
     * @param {string|number} collaborationId - Collaboration ID
     * @param {string|number} memberId - Member ID to update
     * @param {Object} data - Update data
     * @param {string} data.role - New role
     * @param {number} data.commission_percentage - New percentage
     * @param {number} data.commission_fixed_amount - New fixed amount
     * @param {string} data.contribution_notes - Updated contribution notes
     * @param {string} data.contribution_level - Updated contribution level
     * @returns {Promise<Object>} Updated member
     */
    updateMember: async (collaborationId, memberId, data) => {
        try {
            const response = await axiosInstance.put(
                `/leadflow/collaborations/${collaborationId}/members/${memberId}`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.updateMember:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Complete collaboration and distribute commissions
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} data - Completion data
     * @param {number} data.loan_amount_disbursed - Final disbursed amount
     * @param {number} data.total_commission - Total commission earned
     * @param {string} data.notes - Completion notes
     * @returns {Promise<Object>} Completion result with distribution details
     */
    completeCollaboration: async (collaborationId, data) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/complete`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.completeCollaboration:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Cancel a collaboration (owner only)
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} data - Cancellation data
     * @param {string} data.reason - Cancellation reason
     * @param {string} data.notes - Additional notes
     * @returns {Promise<Object>} Cancellation result
     */
    cancelCollaboration: async (collaborationId, data) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/cancel`,
                data
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.cancelCollaboration:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Raise a dispute on a collaboration
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} disputeData - Dispute details
     * @param {string} disputeData.reason - Dispute reason (from DISPUTE_REASONS)
     * @param {string} disputeData.description - Detailed description (min 50 chars)
     * @param {string} disputeData.requested_resolution - Requested resolution (from DISPUTE_RESOLUTIONS)
     * @param {number} disputeData.against_member_id - Optional: specific member dispute is against
     * @returns {Promise<Object>} Dispute result
     */
    raiseDispute: async (collaborationId, disputeData) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/dispute`,
                disputeData
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.raiseDispute:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Preview commission distribution before completing
     * @param {string|number} collaborationId - Collaboration ID
     * @param {Object} previewData - Preview parameters
     * @param {number} previewData.loan_amount_disbursed - Disbursed amount
     * @param {number} previewData.total_commission - Total commission
     * @returns {Promise<Object>} Distribution preview with member-wise breakdown
     */
    previewCommission: async (collaborationId, previewData = {}) => {
        try {
            const params = new URLSearchParams();

            if (previewData.loan_amount_disbursed) {
                params.append('loan_amount_disbursed', previewData.loan_amount_disbursed);
            }
            if (previewData.total_commission) {
                params.append('total_commission', previewData.total_commission);
            }

            const query = params.toString();
            const url = query
                ? `/leadflow/collaborations/${collaborationId}/commission-preview?${query}`
                : `/leadflow/collaborations/${collaborationId}/commission-preview`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('collaborationService.previewCommission:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Search for users to invite (uses profile search API)
     * @param {string} query - Search query (name, email, mobile)
     * @param {Object} options - Search options
     * @param {number} options.limit - Max results
     * @param {Array} options.exclude_ids - Profile IDs to exclude
     * @returns {Promise<Object>} Search results
     */
    searchUsersToInvite: async (query, options = {}) => {
        try {
            const params = new URLSearchParams();
            params.append('q', query);

            if (options.limit) {
                params.append('limit', options.limit);
            }
            if (options.exclude_ids?.length) {
                params.append('exclude_ids', options.exclude_ids.join(','));
            }

            const response = await axiosInstance.get(`/leadflow/profiles/search?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('collaborationService.searchUsersToInvite:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // MODULE 5C: COLLABORATIVE LEAD MANAGEMENT
    // ================================

    /**
     * Update lead data in a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {Object} leadData - Lead fields to update
     * @returns {Promise<Object>} Updated collaboration data
     */
    updateLeadData: async (collaborationId, leadData) => {
        try {
            const response = await axiosInstance.put(
                `/leadflow/collaborations/${collaborationId}/lead`,
                leadData
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.updateLeadData:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get activities for a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {Object} options - Pagination options (limit, offset)
     * @returns {Promise<Object>} Activities list
     */
    getActivities: async (collaborationId, options = {}) => {
        try {
            const params = new URLSearchParams();
            if (options.limit) params.append('limit', options.limit);
            if (options.offset) params.append('offset', options.offset);

            const query = params.toString();
            const url = query
                ? `/leadflow/collaborations/${collaborationId}/activities?${query}`
                : `/leadflow/collaborations/${collaborationId}/activities`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('collaborationService.getActivities:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get documents for a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {string} category - Optional category filter
     * @returns {Promise<Object>} Documents list
     */
    getDocuments: async (collaborationId, category = null) => {
        try {
            const params = new URLSearchParams();
            if (category) params.append('category', category);

            const query = params.toString();
            const url = query
                ? `/leadflow/collaborations/${collaborationId}/documents?${query}`
                : `/leadflow/collaborations/${collaborationId}/documents`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('collaborationService.getDocuments:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Upload a document to a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {File} file - File to upload
     * @param {Object} documentData - Document metadata
     * @param {string} documentData.category - Document category
     * @param {string} documentData.document_type - Document type code (e.g., 'pan', 'bank_statement')
     * @param {string} documentData.document_name - Custom document name
     * @param {string} documentData.description - Document description
     * @param {string} documentData.sub_type - Sub-type for documents with variants (e.g., '3_months', 'utility_bill')
     * @returns {Promise<Object>} Uploaded document
     */
    uploadDocument: async (collaborationId, file, documentData = {}) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (documentData.document_definition_id) formData.append('document_definition_id', documentData.document_definition_id);
            if (documentData.description) formData.append('description', documentData.description);

            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/documents`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.uploadDocument:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Delete a document from a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {string} documentId - Document ID
     * @returns {Promise<Object>} Result
     */
    deleteDocument: async (collaborationId, documentId) => {
        try {
            const response = await axiosInstance.delete(
                `/leadflow/collaborations/${collaborationId}/documents/${documentId}`
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.deleteDocument:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get notes for a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @returns {Promise<Object>} Notes list
     */
    getNotes: async (collaborationId) => {
        try {
            const response = await axiosInstance.get(
                `/leadflow/collaborations/${collaborationId}/notes`
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.getNotes:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Add a note to a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {Object} noteData - Note content and options
     * @returns {Promise<Object>} Created note
     */
    addNote: async (collaborationId, noteData) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/collaborations/${collaborationId}/notes`,
                noteData
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.addNote:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Delete a note from a collaboration
     * @param {string} collaborationId - Collaboration ID
     * @param {string} noteId - Note ID
     * @returns {Promise<Object>} Result
     */
    deleteNote: async (collaborationId, noteId) => {
        try {
            const response = await axiosInstance.delete(
                `/leadflow/collaborations/${collaborationId}/notes/${noteId}`
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.deleteNote:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Toggle pin status of a note
     * @param {string} collaborationId - Collaboration ID
     * @param {string} noteId - Note ID
     * @returns {Promise<Object>} Updated note
     */
    toggleNotePin: async (collaborationId, noteId) => {
        try {
            const response = await axiosInstance.put(
                `/leadflow/collaborations/${collaborationId}/notes/${noteId}/pin`
            );
            return response.data;
        } catch (error) {
            console.error('collaborationService.toggleNotePin:', error);
            throw error.response?.data || error;
        }
    }
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get status configuration
 * @param {string} status - Status value
 * @returns {Object} Status config with color, label, icon
 */
export const getStatusConfig = (status) => {
    return COLLABORATION_STATUS[status?.toLowerCase()] || {
        value: status,
        label: status || 'Unknown',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-question',
        severity: 'secondary'
    };
};

/**
 * Get role configuration
 * @param {string} role - Role value
 * @returns {Object} Role config with color, label, icon
 */
export const getRoleConfig = (role) => {
    return MEMBER_ROLES[role?.toLowerCase()] || {
        value: role,
        label: role || 'Unknown',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        icon: 'pi-user',
        description: 'Unknown role'
    };
};

/**
 * Get commission model configuration
 * @param {string} model - Commission model value
 * @returns {Object} Model config with icon, label, description
 */
export const getCommissionModelConfig = (model) => {
    return COMMISSION_MODELS[model] || {
        value: model,
        label: model?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        icon: 'pi-money-bill',
        description: 'Unknown commission model'
    };
};

/**
 * Get contribution level configuration
 * @param {string} level - Contribution level value
 * @returns {Object} Level config with color, label
 */
export const getContributionLevelConfig = (level) => {
    return CONTRIBUTION_LEVELS[level?.toLowerCase()] || {
        value: level,
        label: level || 'Unknown',
        color: '#6B7280',
        bgColor: '#F3F4F6',
        description: 'Unknown contribution level'
    };
};

/**
 * Get member status configuration
 * @param {string} status - Member status value
 * @returns {Object} Status config
 */
export const getMemberStatusConfig = (status) => {
    return MEMBER_STATUS[status?.toLowerCase()] || {
        value: status,
        label: status || 'Unknown',
        color: '#6B7280',
        icon: 'pi-question'
    };
};

/**
 * Format commission based on model
 * @param {string} model - Commission model
 * @param {Object} data - Commission data
 * @param {number} data.percentage - Percentage value
 * @param {number} data.fixedAmount - Fixed amount value
 * @param {Array} data.milestones - Milestones array
 * @returns {string} Formatted commission string
 */
export const formatCommission = (model, data) => {
    switch (model) {
        case 'percentage_split':
            return `${data.percentage || 0}%`;
        case 'fixed_amount':
            return formatCurrency(data.fixedAmount || data.fixed_amount || 0);
        case 'milestone_based':
            const milestones = data.milestones || [];
            const total = milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
            return `${milestones.length} milestones (${formatCurrency(total)})`;
        default:
            return '-';
    }
};

/**
 * Format currency in Indian Rupee format
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Format large amounts with Lakhs/Crores
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted amount
 */
export const formatLargeAmount = (amount) => {
    if (!amount) return '-';

    if (amount >= 10000000) {
        return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
        return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
        return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount}`;
};

/**
 * Format percentage value
 * @param {number} value - Percentage value
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 0) => {
    if (value === null || value === undefined) return '-';
    return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Calculate total commission from members
 * @param {Array} members - Array of collaboration members
 * @param {string} model - Commission model
 * @returns {Object} Total commission info
 */
export const calculateTotalCommission = (members, model) => {
    if (!members?.length) {
        return { total: 0, allocated: 0, remaining: 0 };
    }

    let allocated = 0;

    switch (model) {
        case 'percentage_split':
            allocated = members.reduce((sum, m) => sum + (m.commission_percentage || 0), 0);
            return {
                total: 100,
                allocated,
                remaining: 100 - allocated,
                unit: '%'
            };
        case 'fixed_amount':
            allocated = members.reduce((sum, m) => sum + (m.commission_fixed_amount || 0), 0);
            return {
                total: null, // No fixed total for fixed amounts
                allocated,
                remaining: null,
                unit: '₹'
            };
        case 'milestone_based':
            members.forEach(m => {
                if (m.milestones?.length) {
                    allocated += m.milestones.reduce((sum, ms) => sum + (ms.amount || 0), 0);
                }
            });
            return {
                total: null,
                allocated,
                remaining: null,
                unit: '₹'
            };
        default:
            return { total: 0, allocated: 0, remaining: 0 };
    }
};

/**
 * Check if user can perform action on collaboration
 * @param {Object} collaboration - Collaboration object
 * @param {Object} currentUser - Current user object
 * @param {string} action - Action to check (invite, remove, complete, cancel, dispute, leave)
 * @returns {boolean} Whether action is allowed
 */
export const canPerformAction = (collaboration, currentUser, action) => {
    if (!collaboration || !currentUser) return false;

    const isOwner = collaboration.owner_profile_id === currentUser.profile_id ||
                    collaboration.members?.some(m => m.profile_id === currentUser.profile_id && m.role === 'owner');
    const isMember = collaboration.members?.some(m => m.profile_id === currentUser.profile_id);
    const isActive = collaboration.status === 'active';

    switch (action) {
        case 'invite':
        case 'remove':
        case 'complete':
        case 'cancel':
            return isOwner && isActive;
        case 'dispute':
            return isMember && isActive;
        case 'leave':
            return isMember && !isOwner && isActive;
        case 'view':
            return isMember;
        default:
            return false;
    }
};

/**
 * Get milestone completion status
 * @param {Array} milestones - Array of milestones
 * @returns {Object} Completion info
 */
export const getMilestoneStatus = (milestones) => {
    if (!milestones?.length) {
        return { completed: 0, total: 0, percentage: 0, text: 'No milestones' };
    }

    const completed = milestones.filter(m => m.completed || m.status === 'completed').length;
    const total = milestones.length;
    const percentage = Math.round((completed / total) * 100);

    return {
        completed,
        total,
        percentage,
        text: `${completed}/${total} completed`
    };
};

/**
 * Format relative time
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Get activity icon based on activity type
 * @param {string} activityType - Type of activity
 * @returns {string} PrimeIcons class
 */
export const getActivityIcon = (activityType) => {
    const iconMap = {
        'created': 'pi-plus-circle',
        'member_invited': 'pi-envelope',
        'member_joined': 'pi-user-plus',
        'member_left': 'pi-sign-out',
        'member_removed': 'pi-user-minus',
        'member_updated': 'pi-pencil',
        'status_changed': 'pi-refresh',
        'milestone_completed': 'pi-flag',
        'dispute_raised': 'pi-exclamation-triangle',
        'completed': 'pi-check-circle',
        'cancelled': 'pi-times-circle',
        'commission_distributed': 'pi-wallet'
    };

    return iconMap[activityType] || 'pi-info-circle';
};

/**
 * Format activity description
 * @param {Object} activity - Activity object
 * @returns {string} Human-readable activity description
 */
export const formatActivityText = (activity) => {
    const { action_type, actor_name, target_name, old_value, new_value } = activity;

    const descriptions = {
        'created': `${actor_name} created the collaboration`,
        'member_invited': `${actor_name} invited ${target_name}`,
        'member_joined': `${target_name} joined the collaboration`,
        'member_left': `${target_name} left the collaboration`,
        'member_removed': `${actor_name} removed ${target_name}`,
        'member_updated': `${actor_name} updated ${target_name}'s terms`,
        'status_changed': `Status changed from ${old_value} to ${new_value}`,
        'milestone_completed': `${actor_name} completed milestone: ${new_value}`,
        'dispute_raised': `${actor_name} raised a dispute`,
        'completed': `${actor_name} completed the collaboration`,
        'cancelled': `${actor_name} cancelled the collaboration`,
        'commission_distributed': `Commissions distributed to all members`
    };

    return descriptions[action_type] || `${actor_name} performed ${action_type}`;
};

export default collaborationService;
